
import { useState } from 'react';
import { Game } from "@/types/game";
import { useFormReset } from './hooks/useFormReset';
import { useImagePreview } from './hooks/useImagePreview';
import { createGame, updateGame } from './services/gameService';
import { toast } from "@/components/ui/use-toast";

interface UseGameFormProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isEditMode: boolean;
  currentGame: Game | null;
  onGameSaved: () => void;
}

export const useGameForm = ({
  isOpen,
  setIsOpen,
  isEditMode,
  currentGame,
  onGameSaved
}: UseGameFormProps) => {
  // Form state management with reset functionality
  const {
    gameName, setGameName,
    coverUrl, setCoverUrl,
    coverFile, setCoverFile,
    previewUrl, setPreviewUrl,
    shouldRemoveCover
  } = useFormReset({ isOpen, isEditMode, currentGame });
  
  // Generate image preview when file is selected
  useImagePreview(coverFile, setPreviewUrl);
  
  // Upload state
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!gameName.trim()) {
      toast({
        title: "Erro",
        description: "O nome do jogo é obrigatório.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    
    try {
      let success = false;
      
      if (isEditMode && currentGame) {
        // Handle edit mode
        console.log(`Submitting update for game: ${currentGame.id}`, {
          newName: gameName,
          coverFile: coverFile ? coverFile.name : 'null',
          currentCoverUrl: coverUrl,
          shouldRemoveCover: shouldRemoveCover
        });
        success = await updateGame(currentGame, gameName, coverFile, coverUrl, shouldRemoveCover);
      } else {
        // Handle create mode
        console.log(`Submitting new game: ${gameName}`, {
          coverFile: coverFile ? coverFile.name : 'null'
        });
        success = await createGame(gameName, coverFile);
      }

      // Refresh games list and close dialog if successful
      if (success) {
        setIsOpen(false);
        onGameSaved();
      }
    } catch (error) {
      console.error('Error during form submission:', error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao salvar o jogo.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  return {
    gameName,
    setGameName,
    coverUrl,
    setCoverUrl,
    coverFile, 
    setCoverFile,
    previewUrl,
    setPreviewUrl,
    uploading,
    handleSubmit
  };
};
